'use client';

import { useTranslations } from 'next-intl';
import { DocsLinks } from '@/components/how-it-works/docs-links';
import {
  DocumentValidationIcon,
  FavouriteIcon,
  SparklesIcon,
  UserMultipleIcon,
} from '@/components/ui/icon/glyphs';
import { InfoModal } from '@/components/ui/info-modal';

interface ProposalRatingHowItWorksModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Explains the "Proposal rating" (Good / Medium / Bad) shown on the proposal
 * detail page. The rating is the backend's `computeProposalScore` — a weighted
 * sum of quality-of-creation signals (cover/outcome images, description &
 * label quality, vote ratio, backer count, anti-whale spread, debate spread,
 * proposer identity, uniqueness). It deliberately excludes how much money the
 * proposal has raised, so the copy frames it as a quality guide, not a funding
 * indicator.
 */
export function ProposalRatingHowItWorksModal({
  open,
  onOpenChange,
}: ProposalRatingHowItWorksModalProps): React.ReactNode {
  const t = useTranslations('proposal_rating_how_it_works');

  return (
    <InfoModal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={t('title')}
      description={t('description')}
      footer={<InfoModal.Footer cta={t('got_it')} onClose={(): void => onOpenChange(false)} />}
    >
      <InfoModal.IconList>
        <InfoModal.IconItem
          icon={DocumentValidationIcon}
          title={t('details_title')}
          description={t('details_description')}
        />
        <InfoModal.IconItem
          icon={FavouriteIcon}
          title={t('community_title')}
          description={t('community_description')}
        />
        <InfoModal.IconItem
          icon={UserMultipleIcon}
          title={t('spread_title')}
          description={t('spread_description')}
        />
        <InfoModal.IconItem
          icon={SparklesIcon}
          title={t('unique_title')}
          description={t('unique_description')}
        />
      </InfoModal.IconList>
      <InfoModal.Callout>{t('info')}</InfoModal.Callout>
      <DocsLinks docsPath="/for-creators" />
    </InfoModal>
  );
}
